import { useEffect, useLayoutEffect, useState } from "react";
import { ConfigProvider, theme as antdTheme } from "antd";

import {
  applyThemeClassToRoot,
  readStoredTheme,
  resolveTheme,
  subscribeToSystemTheme,
  THEME_DEFAULT,
  THEME_STORAGE_KEY,
  type Theme,
} from "@/lib/theme";
import { ThemeProviderContext } from "./theme-context";
import { RAISY_TYPOGRAPHY_PX } from "@/theme";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

/**
 * THE single theme provider for the whole app (admin/CRM shell and public
 * landing pages alike). Owns the light/dark/system state, persists it to
 * `localStorage['theme']`, applies the resolved class to `<html>` before
 * paint, and drives the AntD `ConfigProvider` algorithm from the resolved
 * theme.
 *
 * @internal
 */
export function ThemeProvider({
  children,
  defaultTheme = THEME_DEFAULT,
  storageKey = THEME_STORAGE_KEY,
  ...props
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme());

  const setTheme = (next: Theme) => {
    try {
      localStorage.setItem(storageKey, next);
    } catch {
      // Storage may be unavailable (private mode); keep in-memory state only.
    }
    setThemeState(next);
  };

  const resolvedTheme = resolveTheme(theme);

  // Apply the theme class as early as possible (before paint).
  useLayoutEffect(() => {
    applyThemeClassToRoot(resolvedTheme);
  }, [resolvedTheme]);

  // Keep the DOM in sync if the OS preference changes while mode is "system".
  // resolvedTheme also recomputes on system change, so the layout effect keeps
  // the state-derived class in sync; the subscription guarantees the DOM
  // updates even if matchMedia state is stale.
  useEffect(() => {
    if (theme !== "system") return;
    return subscribeToSystemTheme(applyThemeClassToRoot);
  }, [theme]);

  const value = {
    theme,
    resolvedTheme,
    setTheme,
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      <ConfigProvider
        theme={{
          algorithm:
            resolvedTheme === "dark"
              ? antdTheme.darkAlgorithm
              : antdTheme.defaultAlgorithm,
          token: {
            colorPrimary: "#6366f1",
            borderRadius: 8,
            // Centralised typography (see raisy_FE/src/index.css @theme inline and
            // @/theme RAISY_TYPOGRAPHY_PX). Keeps Antd components on the same scale as
            // the Tailwind design system / entity wizard.
            ...RAISY_TYPOGRAPHY_PX,
          },
        }}
      >
        {children}
      </ConfigProvider>
    </ThemeProviderContext.Provider>
  );
}
